import api from './api';

export interface InvoiceLineItemPayload {
  name: string;
  quantity: number | '';
  unit: string;
  unitNetPrice: number | '';
  unitGrossPrice: number | '';
  vatRate: number | string;
  netValue: number | '';
  grossValue: number | '';
}

export interface CreateTransferInvoicePayload {
  document_type: 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)';
  document_kind?: 'vat' | 'advance' | 'proforma' | 'correction';
  document_date: string; // YYYY-MM-DD
  received_date: string; // YYYY-MM-DD
  project_id: string;
  contractor_id?: string | number | null;
  contractor_name: string;
  account_number?: string;
  nip?: string;
  invoice_number?: string;
  warehouse?: string;
  net_amount: number;
  vat_rate: '0' | '8' | '23' | 'zw' | 'różne';
  gross_amount: number;
  calc_mode?: 'net' | 'gross' | 'none';
  due_date: string; // YYYY-MM-DD
  priority: 'zwykły' | 'pilny';
  comment?: string;
  /**
   * Identyfikator użytkownika, który „wprowadził" fakturę (kolumna „W" w tabeli).
   * W formularzach domyślnie ustawiany na aktualnie zalogowanego użytkownika,
   * ale operator może go zmienić (np. wprowadza fakturę za kogoś).
   */
  created_by_user_id?: string | null;
  pdf: File;
  items?: InvoiceLineItemPayload[];
}

export async function createTransferInvoice(payload: CreateTransferInvoicePayload, googleAccessToken?: string) {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (k === 'pdf' || k === 'items') return;
    if (v !== undefined && v !== null) form.append(k, String(v));
  });
  if (payload.items && payload.items.length > 0) {
    form.append('items', JSON.stringify(payload.items));
  }
  form.append('pdf', payload.pdf);
  const { data } = await api.post('/transfer-invoices', form, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(googleAccessToken ? { 'x-google-access-token': googleAccessToken } : {}),
    },
  });
  return data;
}

export interface TransferInvoiceDto {
  id: number;
  document_type: 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)';
  document_kind?: 'vat' | 'advance' | 'proforma' | 'correction';
  document_date: string;
  received_date: string;
  due_date: string;
  paid_date?: string | null;
  project_id: string;
  contractor_name: string;
  net_amount: number;
  vat_rate: '0' | '8' | '23' | 'zw' | 'różne';
  gross_amount: number;
  priority: 'zwykły' | 'pilny';
  status: 'Pilny' | 'Wprowadzono' | 'Zatwierdzone' | 'Odrzucone' | 'Uzupełnić';
  google_drive_file_url?: string | null;
  created_at: string;
}

export async function listTransferInvoices(): Promise<TransferInvoiceDto[]> {
  const { data } = await api.get('/transfer-invoices');
  return data?.data || [];
}

export async function quickApproveInvoice(id: number | string, decision: 'approved'|'rejected'|'needs_info', comment?: string) {
  const { data } = await api.post(`/transfer-invoices/${id}/quick-approval`, { decision, comment });
  return data;
}

export async function addInvoiceComment(id: number | string, message: string) {
  const { data } = await api.post(`/transfer-invoices/${id}/comment`, { message });
  return data;
}

// Oznacz powód (komentarz) jako rozwiązany / cofnij oznaczenie
export async function resolveInvoiceComment(id: number | string, commentId: string, resolution?: string) {
  const { data } = await api.post(`/transfer-invoices/${id}/comments/${commentId}/resolve`, resolution ? { resolution } : {});
  return data;
}

export async function unresolveInvoiceComment(id: number | string, commentId: string) {
  const { data } = await api.post(`/transfer-invoices/${id}/comments/${commentId}/unresolve`, {});
  return data;
}

// Zgłoś fakturę do ponownej weryfikacji (resetuje rejected/needs_info → pending)
// `force=true` (admin) wymusza, gdy zostały nierozwiązane powody.
export async function resubmitInvoice(
  id: number | string,
  options?: { comment?: string; force?: boolean }
) {
  const { data } = await api.post(`/transfer-invoices/${id}/resubmit`, options || {});
  return data;
}

// Format znormalizowanego wpisu metadanych komentarza zwracanego przez backend
export interface CommentMetaEntry {
  type?: 'reason' | 'response' | 'system';
  reason_kind?: 'rejected' | 'needs_info';
  role?: string | null;
  author_id?: string | null;
  author?: string | null;
  created_at?: string | null;
  resolved?: boolean;
  resolved_by?: string | null;
  resolved_by_name?: string | null;
  resolved_at?: string | null;
  resolution_text?: string | null;
}

export type CommentsMeta = Record<string, CommentMetaEntry>;

export interface UpdateTransferInvoicePayload extends Partial<CreateTransferInvoicePayload> {}

export async function updateTransferInvoice(id: number | string, payload: UpdateTransferInvoicePayload) {
  const form = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (k === 'pdf' || k === 'items') return;
    if (v !== undefined && v !== null) form.append(k, String(v));
  });
  if (payload.items && payload.items.length > 0) {
    form.append('items', JSON.stringify(payload.items));
  }
  if (payload.pdf) form.append('pdf', payload.pdf);
  const { data } = await api.patch(`/transfer-invoices/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getInvoiceItems(id: number | string) {
  const { data } = await api.get(`/transfer-invoices/${id}/items`);
  return data?.data || [];
}

export async function deleteTransferInvoice(id: number | string) {
  const { data } = await api.delete(`/transfer-invoices/${id}`);
  return data;
}

export async function setInvoiceBooked(id: number | string, booked: boolean) {
  const { data } = await api.patch(`/transfer-invoices/${id}/booked`, { booked });
  return data;
}

export async function syncInvoicesBookedFromMssql(all?: boolean): Promise<{ checked: number; updated: number; updatedIds?: Array<number|string> }> {
  const url = `/transfer-invoices/sync/booked-from-mssql${all ? '?all=1' : ''}`;
  const { data } = await api.post(url, {});
  return data?.data || { checked: 0, updated: 0 };
}

// === Rozrachunki (Settlements) ===

export interface SettlementRow {
  id: number;
  document_type: 'Przelew' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)' | 'Proforma';
  document_kind?: 'vat' | 'advance' | 'proforma' | 'correction';
  created_date: string;
  project_code: string;
  due_date: string;
  paid_date?: string | null;
  contractor: string;
  nip?: string | null;
  contractor_address?: string | null;
  contractor_postal_city?: string | null;
  ksef_number?: string | null;
  invoice_number?: string | null;
  gross_amount: number;
  net_amount: number;
  vat_rate: string;
  status: string;
  created_at: string;
  warehouse?: string | null;
  account_number?: string | null;
  project_name?: string | null;
  google_drive_file_id?: string | null;
  google_drive_file_url?: string | null;
  reconciliation_bank_name?: string | null;
  reconciliation_statement_number?: string | null;
  reconciliation_statement_date?: string | null;
  reconciliation_transaction_id?: number | null;
  reconciled_at?: string | null;
  settled: boolean;
}

export async function listSettlements(): Promise<SettlementRow[]> {
  const { data } = await api.get('/transfer-invoices/settlements');
  return data?.data || [];
}

export interface SettleInvoicePayload {
  paid_date?: string;
  payment_method?: string;
  currency?: string;
  amount_to_pay?: number;
  amount_vat?: number;
  amount_from_vat_account?: number;
  transfer_title?: string;
  unitresco_account?: string;
}

export async function settleInvoice(id: number | string, payload?: SettleInvoicePayload): Promise<void> {
  await api.patch(`/transfer-invoices/settlements/${id}/settle`, payload || {});
}

export async function unsettleInvoice(id: number | string): Promise<void> {
  await api.patch(`/transfer-invoices/settlements/${id}/unsettle`);
}

export async function reconcileSettlement(id: number | string, transactionId: number | string): Promise<any> {
  const { data } = await api.patch(`/transfer-invoices/settlements/${id}/reconcile`, { transaction_id: transactionId });
  return data?.data;
}

export async function unreconcileSettlement(id: number | string): Promise<void> {
  await api.patch(`/transfer-invoices/settlements/${id}/unreconcile`);
}

// ─── Reconciliation suggestions ───────────────────────────────────────────────

export interface ReconciliationSuggestion {
  transaction_id: number | string;
  settlement_id: number | string;
  score: number;
}

export async function loadReconciliationSuggestions(): Promise<ReconciliationSuggestion[]> {
  const { data } = await api.get('/transfer-invoices/reconciliation-suggestions');
  return data?.data || [];
}

export async function saveReconciliationSuggestions(suggestions: ReconciliationSuggestion[]): Promise<void> {
  await api.post('/transfer-invoices/reconciliation-suggestions', { suggestions });
}

export async function deleteReconciliationSuggestion(txId: number | string, settId: number | string): Promise<void> {
  await api.delete(`/transfer-invoices/reconciliation-suggestions/${txId}/${settId}`);
}

// ─── Reconciliation links (M:N) ──────────────────────────────────────────────

export interface ReconciliationLink {
  transaction_id: number | string;
  settlement_id: number | string;
}

export async function listReconciliationLinks(): Promise<ReconciliationLink[]> {
  const { data } = await api.get('/transfer-invoices/reconciliation-links');
  return data?.data || [];
}

export async function addReconciliationLink(txId: number | string, settId: number | string): Promise<void> {
  await api.post('/transfer-invoices/reconciliation-links', { transaction_id: txId, settlement_id: settId });
}

export async function removeReconciliationLink(txId: number | string, settId: number | string): Promise<void> {
  await api.delete(`/transfer-invoices/reconciliation-links/${txId}/${settId}`);
}
